/**
 * Incidents (WALLIYO-0132, #126).
 *
 * Opening one puts a banner in front of every customer, so the form says so
 * before the button is pressed: a banner is optional, and leaving it empty
 * tracks the incident without telling anybody.
 *
 * The timeline is append-only. Nothing here edits an entry, because the
 * database would refuse it — a post-mortem whose history can be rewritten is
 * not a post-mortem.
 */

import type { Incident, IncidentDetail, IncidentSeverity } from "@wallyo/contracts/console/incidents";
import { useState } from "react";

import { getIncident, listIncidents, openIncident, updateIncident } from "../../api/console-api";
import { useApi } from "../../api/context";
import { t } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import { useAlerts } from "../../shell/alerts";
import { PageTop } from "../../shell/PageTop";
import { LoadFailed, Loading, useLoad } from "../config-parts";
import { ReasonDialogView, useIntentWrite } from "../team/TeamDialogs";

const STATUS_TONE: Record<Incident["status"], string> = {
  open: "bad",
  monitoring: "warn",
  resolved: "ok",
};

function IncidentRow({ incident, onOpen }: { incident: Incident; onOpen: (id: string) => void }) {
  return (
    <li className="role-holder">
      <span className="mono foot">{incident.reference}</span>
      <strong>{incident.title}</strong>
      <span className={`pill ${STATUS_TONE[incident.status]}`}>{t(`incidents.status.${incident.status}`)}</span>
      {incident.severity === "down" ? <span className="pill bad">{t("incidents.severity.down")}</span> : null}
      {incident.bannerFr === null ? <span className="foot">{t("incidents.notAnnounced")}</span> : null}
      <div className="grow" />
      <span className="foot">{formatDateTime(incident.startedAt)}</span>
      <button className="btn quiet small" type="button" onClick={() => onOpen(incident.incidentId)}>
        {t("incidents.open")}
      </button>
    </li>
  );
}

function OpenDialog({ onClose, onDone }: { onClose: () => void; onDone: (incident: IncidentDetail) => void }) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [title, setTitle] = useState("");
  const [severity, setSeverity] = useState<IncidentSeverity>("degraded");
  const [banner, setBanner] = useState("");
  const [reason, setReason] = useState("");

  return (
    <ReasonDialogView
      title={t("incidents.openTitle")}
      body={<p className="meta">{t("incidents.openBody")}</p>}
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("incidents.openSubmit")}
      danger
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={onClose}
      onSubmit={() =>
        void write.run(
          title.trim().length < 4 ? t("incidents.titleNeeded") : null,
          (key) =>
            openIncident(
              client,
              { title: title.trim(), severity, bannerFr: banner.trim() || null, reason: reason.trim() },
              { idempotencyKey: key },
            ),
          ({ incident }) => onDone(incident),
        )
      }
    >
      <div className="field">
        <label htmlFor="incident-title">{t("incidents.field.title")}</label>
        <input
          id="incident-title"
          value={title}
          onChange={(event) => {
            write.edited();
            setTitle(event.target.value);
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="incident-severity">{t("incidents.field.severity")}</label>
        <select
          id="incident-severity"
          value={severity}
          onChange={(event) => setSeverity(event.target.value as IncidentSeverity)}
        >
          <option value="degraded">{t("incidents.severity.degraded")}</option>
          <option value="down">{t("incidents.severity.down")}</option>
        </select>
      </div>
      {/* Empty means nobody is told. Announcing is a decision. */}
      <div className="field">
        <label htmlFor="incident-banner">{t("incidents.field.banner")}</label>
        <textarea id="incident-banner" rows={2} value={banner} onChange={(event) => setBanner(event.target.value)} />
        <span className="meta">{t("incidents.field.bannerHint")}</span>
      </div>
    </ReasonDialogView>
  );
}

export function IncidentsScreen() {
  const { client } = useApi();
  const alerts = useAlerts();
  const [reads, setReads] = useState(0);
  const [opening, setOpening] = useState(false);
  const [detail, setDetail] = useState<IncidentDetail | null>(null);

  const load = useLoad(() => listIncidents(client), String(reads));
  const data = load.state.data;
  const reload = () => setReads((count) => count + 1);

  const show = (incidentId: string) => {
    void getIncident(client, incidentId).then(
      ({ incident }) => setDetail(incident),
      (error: unknown) => alerts.problem(error),
    );
  };

  return (
    <>
      <PageTop title={t("incidents.title")} sub={t("incidents.sub")} />

      <div className="hr-search">
        <div className="grow" />
        <button className="btn primary" type="button" onClick={() => setOpening(true)}>
          {t("incidents.openOne")}
        </button>
      </div>

      {load.state.status === "failed" && !data ? (
        <LoadFailed
          message={t("incidents.loadFailed")}
          retry={t("hr.retry")}
          error={load.state.error}
          onRetry={load.reload}
        />
      ) : null}
      {!data && load.state.status === "loading" ? <Loading text={t("hr.loading")} /> : null}

      {data ? (
        <>
          <section className="card">
            <h2>{t("incidents.happeningNow")}</h2>
            {data.open.length === 0 ? (
              <div className="empty">{t("incidents.allWell")}</div>
            ) : (
              <ul className="hr-plain">
                {data.open.map((incident) => (
                  <IncidentRow key={incident.incidentId} incident={incident} onOpen={show} />
                ))}
              </ul>
            )}
          </section>

          {data.recent.length > 0 ? (
            <section className="card">
              <h2>{t("incidents.over")}</h2>
              <ul className="hr-plain">
                {data.recent.map((incident) => (
                  <IncidentRow key={incident.incidentId} incident={incident} onOpen={show} />
                ))}
              </ul>
            </section>
          ) : null}
        </>
      ) : null}

      {opening ? (
        <OpenDialog
          onClose={() => setOpening(false)}
          onDone={(incident) => {
            setOpening(false);
            setDetail(incident);
            alerts.ok(t("incidents.opened", { reference: incident.reference }));
            reload();
          }}
        />
      ) : null}

      {detail ? (
        <UpdateDialog
          incident={detail}
          onClose={() => setDetail(null)}
          onDone={(incident) => {
            setDetail(null);
            alerts.ok(t("incidents.updated", { reference: incident.reference }));
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function UpdateDialog({
  incident,
  onClose,
  onDone,
}: {
  incident: IncidentDetail;
  onClose: () => void;
  onDone: (incident: IncidentDetail) => void;
}) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [status, setStatus] = useState(incident.status);
  const [note, setNote] = useState("");
  const [cause, setCause] = useState(incident.cause ?? "");
  const [reason, setReason] = useState("");

  return (
    <ReasonDialogView
      title={`${incident.reference} · ${incident.title}`}
      body={
        <>
          <p className="meta">{t("incidents.started", { when: formatDateTime(incident.startedAt) })}</p>
          {/* The timeline, which nothing can rewrite. */}
          <ul className="hr-plain">
            {incident.entries.map((entry) => (
              <li key={entry.incidentEntryId} className="role-holder">
                <span className="foot">{formatDateTime(entry.at)}</span>
                <strong>{entry.by}</strong>
                <bdi className="foot">{entry.body}</bdi>
              </li>
            ))}
          </ul>
        </>
      }
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("incidents.updateSubmit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={onClose}
      onSubmit={() =>
        void write.run(
          status === "resolved" && cause.trim().length === 0 ? t("incidents.causeNeeded") : null,
          (key) =>
            updateIncident(
              client,
              incident.incidentId,
              {
                status,
                note: note.trim() || undefined,
                cause: status === "resolved" ? cause.trim() : undefined,
                version: incident.version,
                reason: reason.trim(),
              },
              { idempotencyKey: key },
            ),
          ({ incident: saved }) => onDone(saved),
        )
      }
    >
      <div className="field">
        <label htmlFor="incident-status">{t("incidents.field.status")}</label>
        <select
          id="incident-status"
          value={status}
          onChange={(event) => setStatus(event.target.value as Incident["status"])}
        >
          <option value="open">{t("incidents.status.open")}</option>
          <option value="monitoring">{t("incidents.status.monitoring")}</option>
          <option value="resolved">{t("incidents.status.resolved")}</option>
        </select>
      </div>
      <div className="field">
        <label htmlFor="incident-note">{t("incidents.field.note")}</label>
        <textarea id="incident-note" rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
        <span className="meta">{t("incidents.field.noteHint")}</span>
      </div>
      {status === "resolved" ? (
        <div className="field">
          <label htmlFor="incident-cause">{t("incidents.field.cause")}</label>
          <textarea id="incident-cause" rows={2} value={cause} onChange={(event) => setCause(event.target.value)} />
        </div>
      ) : null}
    </ReasonDialogView>
  );
}
